'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@/context/cart-context';
import { formatPrice } from '@/lib/shopify';
import type { ProductVariant, Money } from '@/lib/shopify/types';
import type { DigitisingFeeData } from '@/app/products/[handle]/product-page-client';
import type { SizeQuantityLine } from './size-quantity-selector';
import { getCustomizerCartAttributes, hasCustomDesign, type CustomDesignState } from '@/lib/customizer';
import {
  finalizeCustomizerDesign,
  type PendingArtworkFiles,
} from '@/lib/customizer-client';

interface StickyAddToCartProps {
  variant: ProductVariant | null;
  price: Money;
  availableForSale: boolean;
  customSize?: string;
  artworkUrl?: string;
  customDesign?: CustomDesignState;
  productId: string;
  productTitle: string;
  selectedImageUrl?: string;
  selectedImageAlt?: string;
  sizeQuantityLines?: SizeQuantityLine[];
  requireSizeQuantities?: boolean;
  isCustomizerUploading?: boolean;
  pendingArtworkFiles?: PendingArtworkFiles;
  digitisingFees?: {
    front: DigitisingFeeData | null;
    back: DigitisingFeeData | null;
  };
  frontDigitising?: boolean;
  backDigitising?: boolean;
  finishAttribute?: string;
}

export default function StickyAddToCart({
  variant,
  price,
  availableForSale,
  customSize,
  artworkUrl,
  customDesign,
  productId,
  productTitle,
  selectedImageUrl,
  selectedImageAlt,
  sizeQuantityLines = [],
  requireSizeQuantities = false,
  isCustomizerUploading = false,
  pendingArtworkFiles,
  digitisingFees,
  frontDigitising,
  backDigitising,
  finishAttribute,
}: StickyAddToCartProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isFinalizingDesign, setIsFinalizingDesign] = useState(false);
  const [showBlankDesignWarning, setShowBlankDesignWarning] = useState(false);
  const [finalizeError, setFinalizeError] = useState('');
  const { addItem, addItems, isLoading: isCartLoading } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky bar after scrolling past 500px
      setIsVisible(window.scrollY > 500);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const sizeRequired = customSize !== undefined && !customSize;
  const hasSizeQuantityLines = sizeQuantityLines.length > 0;
  const sizeQuantitiesRequired = requireSizeQuantities && !hasSizeQuantityLines;

  const addToCart = async () => {
    if (!variant || !availableForSale || sizeRequired || sizeQuantitiesRequired || isCustomizerUploading || isFinalizingDesign || isCartLoading) return;

    const shouldFinalizeDesign = pendingArtworkFiles !== undefined;
    setIsAdding(true);
    setIsFinalizingDesign(shouldFinalizeDesign);
    setFinalizeError('');
    try {
      const finalized = !shouldFinalizeDesign
        ? { design: customDesign, previewUrl: undefined }
        : await finalizeCustomizerDesign({
            design: customDesign,
            pendingArtworkFiles,
            productTitle,
            selectedImageUrl,
          });

      const attributes: { key: string; value: string }[] = [];
      if (finishAttribute) attributes.push({ key: 'Finish', value: finishAttribute });
      if (customSize) attributes.push({ key: 'Size', value: customSize });
      if (artworkUrl) attributes.push({ key: 'Artwork', value: artworkUrl });
      attributes.push(...getCustomizerCartAttributes(finalized.design));
      if (finalized.previewUrl) attributes.push({ key: 'Design preview', value: finalized.previewUrl });
      if (selectedImageUrl) {
        attributes.push({ key: '_image', value: selectedImageUrl });
        if (selectedImageAlt) attributes.push({ key: '_imageAlt', value: selectedImageAlt });
      }
      // Track which sides this variant wants embroidered. See add-to-cart-button.tsx
      // for the full rationale (drawer surfaces fees under every matching variant;
      // cascade uses these flags to know when no variant still needs the side).
      if (frontDigitising) attributes.push({ key: '_embroideryFront', value: 'true' });
      if (backDigitising) attributes.push({ key: '_embroideryBack', value: 'true' });
      const garmentLines = hasSizeQuantityLines
        ? sizeQuantityLines.map((line) => ({
            variantId: line.variant.id,
            quantity: line.quantity,
            attributes: attributes.length > 0 ? attributes : undefined,
          }))
        : [{ variantId: variant.id, quantity: 1, attributes: attributes.length > 0 ? attributes : undefined }];
      const primaryGarmentVariantId = garmentLines[0]?.variantId || variant.id;

      const needsFees = (frontDigitising && digitisingFees?.front) || (backDigitising && digitisingFees?.back);

      if (needsFees) {
        // _linkedProduct: used by dedup + cascade.
        // _linkedVariant: used by the drawer for visual nesting under the
        // variant that triggered the fee. See add-to-cart-button.tsx.
        const feeAttributes = [
          { key: 'For', value: productTitle },
          { key: '_linkedProduct', value: productId },
          { key: '_linkedVariant', value: primaryGarmentVariantId },
        ];
        const lines: { variantId: string; quantity: number; attributes?: { key: string; value: string }[] }[] = [...garmentLines];
        if (frontDigitising && digitisingFees?.front) {
          lines.push({ variantId: digitisingFees.front.variantId, quantity: 1, attributes: feeAttributes });
        }
        if (backDigitising && digitisingFees?.back) {
          lines.push({ variantId: digitisingFees.back.variantId, quantity: 1, attributes: feeAttributes });
        }
        await addItems(lines);
      } else if (hasSizeQuantityLines) {
        await addItems(garmentLines);
      } else {
        await addItem(variant.id, 1, attributes.length > 0 ? attributes : undefined);
      }

      setShowBlankDesignWarning(false);
    } catch (error) {
      console.error('Failed to add to cart:', error);
      setFinalizeError(error instanceof Error ? error.message : 'Could not prepare your design. Please try again.');
    } finally {
      setIsFinalizingDesign(false);
      setIsAdding(false);
    }
  };

  const handleAddToCart = async () => {
    if (customDesign && !hasCustomDesign(customDesign) && !showBlankDesignWarning) {
      setShowBlankDesignWarning(true);
      document.getElementById('product-customizer')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }

    setShowBlankDesignWarning(false);
    await addToCart();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[var(--color-border)] shadow-lg"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {finalizeError && (
            <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700" role="alert">
              {finalizeError}
            </div>
          )}
          {showBlankDesignWarning && (
            <div className="border-b border-amber-300 bg-amber-50 px-4 py-3">
              <p className="text-sm font-semibold text-black">No design has been added yet.</p>
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                Add artwork, text, or instructions, or continue with the current product options.
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => document.getElementById('product-customizer')?.scrollIntoView({ block: 'center', behavior: 'smooth' })}
                  className="border border-brand-blue bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wide text-brand-blue"
                >
                  Design
                </button>
                <button
                  type="button"
                  onClick={addToCart}
                  disabled={isAdding || isFinalizingDesign || sizeQuantitiesRequired || isCustomizerUploading || isCartLoading}
                  className="bg-brand-blue px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:opacity-60"
                >
                  Continue
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between px-4 py-3 gap-4">
            <div className="flex-shrink-0">
              <span className="text-lg font-semibold">{formatPrice(price)}</span>
            </div>
            <button
              onClick={handleAddToCart}
              disabled={!variant || !availableForSale || isAdding || isFinalizingDesign || sizeRequired || sizeQuantitiesRequired || isCustomizerUploading || isCartLoading}
              className={`flex-1 py-3 text-sm font-semibold uppercase tracking-wider transition-all ${
                !variant || !availableForSale || sizeRequired || sizeQuantitiesRequired || isCustomizerUploading || isFinalizingDesign || isCartLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-brand-blue text-white active:bg-brand-blue-hover'
              }`}
            >
              {isFinalizingDesign ? (
                'Preparing Design...'
              ) : isAdding ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Adding...
                </span>
              ) : isCartLoading ? (
                'Preparing Cart...'
              ) : isCustomizerUploading ? (
                'Uploading Artwork...'
              ) : !availableForSale ? (
                'Out of Stock'
              ) : sizeRequired ? (
                'Select a Size'
              ) : sizeQuantitiesRequired ? (
                'Select Quantities'
              ) : (
                'Add to Cart'
              )}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
